'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import ThemeToggle from '@/components/ui/ThemeToggle';

const navLinks = [
  { label: 'The Firm', href: '/the-firm' },
  { label: 'Practice Areas', href: '/practice-areas' },
  { label: 'Insights', href: '/insights' },
  { label: 'About EasyLaw', href: '/about-easylaw' },
  { label: 'Contact', href: '/contact' },
];

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  return (
    <>
      <header
        suppressHydrationWarning
        className={`fixed top-0 left-0 right-0 z-50 h-20 transition-all duration-500 ${
          scrolled
            ? 'bg-[var(--header-bg)] backdrop-blur-md border-b border-[var(--header-border)] shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <nav suppressHydrationWarning className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          {/* Logo */}
          <Link suppressHydrationWarning href="/" className="flex items-center" aria-label="EasyLaw — Home">
            <div className="flex items-center justify-center h-10 px-4 bg-[var(--primary)] rounded">
              <span className="text-sm font-bold tracking-widest uppercase text-white whitespace-nowrap">EasyLaw</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div suppressHydrationWarning className="hidden md:flex items-center gap-10 text-xs font-semibold tracking-widest uppercase text-[var(--muted-foreground)]">
            {navLinks?.map((link) => (
              <Link
                suppressHydrationWarning
                key={link?.href}
                href={link?.href}
                className="hover:text-[var(--primary)] transition-colors duration-300 underline-hover"
              >
                {link?.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA + Theme Toggle */}
          <div suppressHydrationWarning className="hidden md:flex items-center gap-3">
            <ThemeToggle />
            <Link
              suppressHydrationWarning
              href="/request-legal-assistance"
              className="text-xs font-semibold uppercase tracking-widest bg-[var(--primary)] text-white px-6 py-3 hover:bg-[var(--accent)] transition-colors duration-300 rounded-sm"
            >
              Request Assistance
            </Link>
          </div>

          {/* Mobile: Theme Toggle + Menu Button */}
          <div suppressHydrationWarning className="md:hidden flex items-center gap-2">
            <ThemeToggle />
            <button
              suppressHydrationWarning
              className="flex flex-col gap-1.5 p-2 z-50"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
            >
              <span suppressHydrationWarning className={`block w-6 h-px bg-[var(--foreground)] transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
              <span suppressHydrationWarning className={`block w-6 h-px bg-[var(--foreground)] transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
              <span suppressHydrationWarning className={`block w-6 h-px bg-[var(--foreground)] transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        suppressHydrationWarning
        className={`fixed inset-0 z-40 bg-[var(--background)] flex flex-col justify-center px-8 transition-all duration-500 ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div suppressHydrationWarning className="space-y-8 mb-12">
          {navLinks?.map((link, i) => (
            <Link
              suppressHydrationWarning
              key={link?.href}
              href={link?.href}
              onClick={() => setMenuOpen(false)}
              className="block font-display text-4xl text-[var(--foreground)] hover:text-[var(--primary)] transition-colors duration-300"
              style={{ transitionDelay: menuOpen ? `${i * 60}ms` : '0ms' }}
            >
              {link?.label}
            </Link>
          ))}
        </div>
        <Link
          suppressHydrationWarning
          href="/request-legal-assistance"
          onClick={() => setMenuOpen(false)}
          className="inline-block text-sm font-semibold uppercase tracking-widest bg-[var(--primary)] text-white px-8 py-4 hover:bg-[var(--accent)] transition-colors duration-300 w-fit rounded-sm"
        >
          Request Legal Assistance
        </Link>
        <div suppressHydrationWarning className="mt-8 pt-8 border-t border-[var(--border)]">
          <p suppressHydrationWarning className="text-xs text-[var(--muted-foreground)] tracking-widest uppercase">Available 24 Hours</p>
        </div>
      </div>
    </>
  );
}